/**
 * Tiny fetch client for the LawBee API. Same origin in dev (Vite proxy); `VITE_API_URL` points at
 * the API elsewhere. Every non-2xx response becomes an {@link ApiError} built from the
 * `{ error: { code, message, details } }` body.
 */

export const API_BASE: string = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError(err: unknown, code?: string): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}

/** Field errors from a 400 response, keyed by path or field. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !Array.isArray(err.details)) return {};
  const out: Record<string, string> = {};
  for (const d of err.details as { path?: string; field?: string; message?: string }[]) {
    const key = d.field ?? d.path ?? '';
    if (key && d.message && !out[key]) out[key] = d.message;
  }
  return out;
}

/** Human message for any thrown value. */
export function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) {
    const fields = fieldErrors(err);
    const first = Object.entries(fields)[0];
    if (err.code === 'invalid_input' && first) return `${err.message} ${first[0]}: ${first[1]}`;
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const e = (data && typeof data === 'object' && 'error' in data ? (data as { error: { code?: string; message?: string; details?: unknown } }).error : null) ?? {};
    throw new ApiError(res.status, e.code ?? `http_${res.status}`, e.message ?? (res.statusText || 'Request failed.'), e.details);
  }
  return data as T;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach LawBee. Check your connection and try again.');
  }
  return parse<T>(res);
}

/** POST multipart form data (uploads). Do not set Content-Type: the browser adds the boundary. */
async function upload<T>(path: string, form: FormData, method = 'POST'): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { method, credentials: 'include', body: form, headers: { Accept: 'application/json' } });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach LawBee. Check your connection and try again.');
  }
  return parse<T>(res);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload,
};

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}
